import { createContext, Dispatch, PropsWithChildren, useReducer, useEffect, useState } from 'react'
import { AuthMethod, Token, User } from '../../types/user-types'
import fetchApi from '../services/api'
import { isValidUser } from '../services/user-utils'
import {
  clearFakeAuthToken,
  clearForwardingPath,
  getFakeAuthToken,
  getTokenAuthToken,
  isValidToken,
  UNKNOWN_TOKEN,
} from '../services/auth-utils'
import handleAPIError from '../components/errors/handler'

export enum UserActionType {
  SET_TOKEN = 'set_token',
  SET_USER_DETAILS = 'set_user_details',
  CLEAR_USER_OBJECT_PERMISSIONS = 'clear_user_object_permissions',
}

export type MenuAction = { type: UserActionType; payload: Partial<User> }

export const userReducer = (state: User, action: MenuAction): User => {
  const { type, payload } = action

  switch (type) {
    case UserActionType.SET_TOKEN:
      return { ...state, ...payload }
    case UserActionType.SET_USER_DETAILS:
      return { ...state, ...payload }
    case UserActionType.CLEAR_USER_OBJECT_PERMISSIONS:
      return { ...state, ...{ objectPermissions: { canEdit: false, canCreate: false, canView: false } } }
    default:
      return state
  }
}

export const UserContext = createContext<{ state: User; dispatch: Dispatch<MenuAction>; isLoading: boolean }>({
  state: { token: UNKNOWN_TOKEN, auth_method: 'Bearer' },
  dispatch: () => undefined,
  isLoading: true,
})

export default function ProvideUserContext({ children }: PropsWithChildren<{}>) {
  const [state, dispatch] = useReducer(userReducer, { token: UNKNOWN_TOKEN, auth_method: 'Bearer' })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // If the token is in UNKNOWN_TOKEN (Initialised state) attempt to get the token from Token Auth or Fake auth.
    const token: Token = state.token === UNKNOWN_TOKEN ? getTokenAuthToken() ?? getFakeAuthToken() : state.token
    // If there is a Token Auth Token then we must be doing AuthToken auth for PDF engine.
    const auth_method: AuthMethod | undefined = getTokenAuthToken() ? 'Token' : state.auth_method

    if (!token) {
      // Keycloak auth sets token to undefined if not logged in
      setIsLoading(false)
      // Ensure user has a CSRF token when unauthenticated
      fetchApi({ url: `users/logged-out`, token })
      dispatch({ type: UserActionType.SET_USER_DETAILS, payload: state })
    } else if (isValidToken(token) && isValidUser(state)) {
      // We have a token, and user info has been retrieved from /api/whoami
      setIsLoading(false)
      clearForwardingPath()
      dispatch({ type: UserActionType.SET_USER_DETAILS, payload: state })
    } else if (isValidToken(token)) {
      // We have a token, but no user info yet
      const user: User = { token, auth_method }
      // No user in context
      fetchApi({
        url: `users/whoami`,
        token,
        auth_method: auth_method,
      })
        .then((userUpdate: User) => {
          clearForwardingPath()
          // Firing this will cause the state to update and that will return the user
          dispatch({
            type: UserActionType.SET_USER_DETAILS,
            payload: { ...user, ...userUpdate },
          })
        })
        .catch((error: any) => {
          handleAPIError(error, { componentStack: 'ProvideUserContext' }, user)
          clearFakeAuthToken()
          dispatch({ type: UserActionType.SET_TOKEN, payload: { token: UNKNOWN_TOKEN } })
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    // Disable due to suggestion of state which causes infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token, dispatch])

  return <UserContext.Provider value={{ state, dispatch, isLoading }}>{children}</UserContext.Provider>
}
